import api from 'api/api'
import { getItemLocalStorage, setItemLocalStorage } from 'utils/local-storage'
import {
  ESTABLISHMENT_APP_VERSION_KEY,
  ESTABLISHMENT_APP_VERSION_DISMISSED_KEY
} from 'constants/establishment'

const APP_VERSION_PATTERN = /^\d+(\.\d+)*$/

/**
 * Cuanto se respeta el "Mas tarde".
 *
 * Sin caducidad el rechazo era permanente: el chequeo solo volvia a avisar si
 * aparecia una version mas nueva, asi que quien rechazaba 2.5.13 se quedaba en
 * 2.5.12 para siempre. Diez minutos callan el aviso lo justo para nomolestar
 * mientras se termina lo que se estaba haciendo.
 */
export const DISMISS_COOLDOWN_MS = 10 * 60 * 1000

/**
 * Compara dos versiones punteadas parte por parte.
 *
 * No se puede usar `>` sobre el texto: "2.5.9" es menor que "2.5.13", pero
 * comparando como cadena "2.5.9" gana porque "9" es mayor que "1". Con numeros
 * tampoco: `Number('2.5.13')` es NaN.
 *
 * @return {number} -1 si a < b, 1 si a > b, 0 si son iguales.
 */
export const compareAppVersions = (a, b) => {
  const parse = (version) => String(version ?? '').trim().split('.').map((part) => parseInt(part, 10) || 0)
  const left = parse(a)
  const right = parse(b)
  const length = Math.max(left.length, right.length)

  for (let i = 0; i < length; i++) {
    const diff = (left[i] || 0) - (right[i] || 0)

    if (diff !== 0) return diff < 0 ? -1 : 1
  }

  return 0
}

/**
 * @return {boolean} true si el valor tiene forma de version (2.5.13).
 *
 * '0' queda fuera a proposito: es el centinela que devuelve el backend cuando no
 * encuentra el archivo VERSION, y no es una version real. Aceptarlo haria que un
 * backend sin VERSION se guardara "0" como si fuera la version del navegador.
 */
export const isValidAppVersion = (version) => {
  const value = String(version ?? '').trim()

  return APP_VERSION_PATTERN.test(value) && value !== '0'
}

/**
 * Version desplegada en el backend, o null si no se pudo leer.
 *
 * Se pide a un endpoint propio y no en un response de datos porque el chequeo
 * corre al cargar la app y en cada cambio de ruta: atarlo a un request de
 * negocio lo haria depender de que esa pantalla se abra y podria disparar
 * acciones dos veces.
 *
 * @return {Promise<string|null>}
 */
export const getRemoteAppVersion = async () => {
  try {
    const { data } = await api.get('/app-version')
    const version = data?.app_version

    return isValidAppVersion(version) ? String(version).trim() : null
  } catch (error) {
    // Sin version conocida el frontend no avisa: es preferible a molestar con
    // "hay una nueva version" cuando no sabemos si la hay.
    console.error(error.response)
    return null
  }
}

/**
 * Version del bundle que esta corriendo ahora en este navegador.
 *
 * @return {string|null} null si el navegador nunca guardo una.
 */
export const getLoadedAppVersion = () => {
  return getItemLocalStorage(ESTABLISHMENT_APP_VERSION_KEY)
}

/**
 * Registra el "Mas tarde" junto con la hora, para que el silencio caduque.
 *
 * @param {string} version
 * @return {void}
 */
export const dismissAppVersion = (version) => {
  setItemLocalStorage(
    ESTABLISHMENT_APP_VERSION_DISMISSED_KEY,
    JSON.stringify({ v: version, at: Date.now() })
  )
}

/**
 * Si todavia toca callarse por esta version.
 *
 * @param {string} version version remota del backend.
 * @return {boolean} true si el usuario ya dijo "Ahora no" y el silencio sigue vigente.
 */
export const isAppVersionDismissed = (version) => {
  const raw = getItemLocalStorage(ESTABLISHMENT_APP_VERSION_DISMISSED_KEY)

  if (!raw) return false

  let saved = null

  try {
    saved = JSON.parse(raw)
  } catch (error) {
    // Formato viejo, que era solo la version sin fecha. Se ignora el silencio
    // en vez de fallar: preguntar de mas es mejor que tragarse una version.
    return false
  }

  if (!saved?.v) return false

  // Una version mas nueva que la rechazada no la silencia: esa es nueva noticia.
  if (compareAppVersions(version, saved.v) > 0) return false

  // Misma version: el silencio se respeta solo mientras no expire.
  return (Date.now() - (Number(saved.at) || 0)) < DISMISS_COOLDOWN_MS
}